import type { RemapEquipmentTypeRequest, RemapEquipmentTypeResponse } from '@rock/types';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Retire one home's custom equipment type onto a catalog type: the server flips the custom's models to
 * the target `typeId` in place (ids are opaque — manual links, candidates and the acquisition job stay
 * put), re-homes document coverage rules naming the custom, flips the home's equipment `typeId`,
 * tombstones the custom (`replacedBy`), and stamps the candidate `promotedTo` when its last live custom
 * is consumed. The home re-eval fires automatically off the equipment writes.
 */
export async function remapEquipmentType(request: RemapEquipmentTypeRequest): Promise<RemapEquipmentTypeResponse> {
    const fn = httpsCallable<RemapEquipmentTypeRequest, RemapEquipmentTypeResponse>(
        getFunctions(),
        'remapEquipmentType',
    );
    const result = await fn(request);
    return result.data;
}

/**
 * Roll several remap results into one, so a bulk pass over a candidate's customs reports a single
 * summary instead of one toast per home. `candidateMarked` is an OR — exactly one call in a pass can
 * consume the candidate's last custom.
 */
export function mergeRemapResults(results: RemapEquipmentTypeResponse[]): RemapEquipmentTypeResponse {
    return results.reduce<RemapEquipmentTypeResponse>(
        (acc, result) => ({
            remappedEquipment: acc.remappedEquipment + count(result.remappedEquipment),
            retypedModels: acc.retypedModels + count(result.retypedModels),
            remappedDocuments: acc.remappedDocuments + count(result.remappedDocuments),
            candidateMarked: acc.candidateMarked || !!result.candidateMarked,
        }),
        { remappedEquipment: 0, retypedModels: 0, remappedDocuments: 0, candidateMarked: false },
    );
}

/**
 * A count from a response the caller may predate. Admin and functions deploy on separate pipelines, so a
 * tab loaded before a functions deploy can receive a shape it was not compiled against — `retypedModels`
 * replaced `rekeyedModels`/`skippedModels` when model ids became opaque. Reading a renamed field as 0 keeps
 * the summary honest-but-low; the alternative is a TypeError inside a toast the operator can't dismiss.
 */
function count(value: number | undefined): number {
    return typeof value === 'number' ? value : 0;
}

/** One-line operator summary of a remap result, shared by every surface that toasts it. */
export function formatRemapSummary(result: RemapEquipmentTypeResponse): string {
    const coverage = count(result.remappedDocuments) ? `, ${count(result.remappedDocuments)} document(s)` : '';
    return `Remapped ${count(result.remappedEquipment)} equipment and ${count(result.retypedModels)} model(s)${coverage}`;
}
